'use client'

import * as React from 'react'
import { CheckCircle2, XCircle, Info, X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { cn } from '@buildbox/shared/utils'

export type ToastVariant = 'success' | 'error' | 'info'
export type ToastItem = { id: string; title?: string; description?: string; variant?: ToastVariant; timeoutMs?: number }

function Icon({ variant }: Readonly<{ variant?: ToastVariant }>) {
	switch (variant) {
		case 'success':
			return <CheckCircle2 className="h-4 w-4 text-primary dark:text-green-500" />
		case 'error':
			return <XCircle className="h-4 w-4 text-primary dark:text-red-500" />
		default:
			return <Info className="h-4 w-4 text-foreground dark:text-blue-500" />
	}
}

export function Toaster({ items, onDismiss }: { items: ToastItem[]; onDismiss?: (id: string) => void }) {
	const [mounted, setMounted] = React.useState(false)
	React.useEffect(() => setMounted(true), [])
	if (!mounted) return null
	return createPortal(
		<div className="fixed top-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2">
			{items.map((t) => (
				<div
					key={t.id}
					className={cn(
						'group relative overflow-hidden rounded-md border bg-background p-3 shadow-lg transition-all duration-200',
						'animate-in fade-in slide-in-from-top-2',
					)}
				>
					<div className="flex items-start gap-2">
						<Icon variant={t.variant} />
						<div className="flex-1">
							{t.title && <div className="text-sm font-medium leading-none mb-0.5">{t.title}</div>}
							{t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
						</div>
						{onDismiss && (
							<button
								aria-label="dismiss"
								className="opacity-60 hover:opacity-100 transition-opacity"
								onClick={() => onDismiss(t.id)}
							>
								<X className="h-4 w-4" />
							</button>
						)}
					</div>
				</div>
			))}
		</div>,
		document.body,
	)
}

export function useLocalToaster() {
	const [items, setItems] = React.useState<ToastItem[]>([])
	const removeById = React.useCallback(
		(toastId: string) => setItems((prev) => prev.filter((x) => x.id !== toastId)),
		[],
	)
	const show = React.useCallback(
		(t: Omit<ToastItem, 'id'>) => {
			const id = crypto.randomUUID()
			const item: ToastItem = { id, ...t }
			setItems((prev) => [...prev, item])
			const timeout = t.timeoutMs ?? 3000
			if (timeout > 0) setTimeout(() => removeById(id), timeout)
		},
		[removeById],
	)
	return { items, show, dismiss: removeById }
}
